import {
    AccountEnvironment,
    UserProfileAttributeMetadata,
    UserRepresentation,
} from "@keycloak/keycloak-account-ui";

const IMMUTABLE_PERSONAL_INFO_FIELDS = new Set(["username", "email"]);

export const isImmutablePersonalInfoField = (attributeName: string) =>
    IMMUTABLE_PERSONAL_INFO_FIELDS.has(attributeName);

export const withImmutablePersonalInfoMetadata = (
    info?: UserRepresentation
): UserRepresentation | undefined => {
    if (!info?.userProfileMetadata?.attributes) {
        return info;
    }

    return {
        ...info,
        userProfileMetadata: {
            ...info.userProfileMetadata,
            attributes: info.userProfileMetadata.attributes.map(
                (attribute: UserProfileAttributeMetadata) =>
                    isImmutablePersonalInfoField(attribute.name)
                        ? { ...attribute, readOnly: true }
                        : attribute
            ),
        },
    };
};

export const sanitizePersonalInfoForSave = (
    info: UserRepresentation | undefined,
    originalInfo?: UserRepresentation
): UserRepresentation | undefined => {
    if (!info) {
        return info;
    }

    const immutableEmail = originalInfo?.email ?? info.email;
    const immutableAttributeEmail =
        originalInfo?.attributes?.email ??
        (info.attributes?.email ? [immutableEmail] : undefined);
    const nextAttributes = immutableAttributeEmail
        ? { ...info.attributes, email: immutableAttributeEmail }
        : info.attributes;
    const isAttributeEmailChanged =
        (info.attributes?.email ?? []).join(",") !== (immutableAttributeEmail ?? []).join(",");

    if (info.email === immutableEmail && !isAttributeEmailChanged) {
        return info;
    }

    return {
        ...info,
        email: immutableEmail,
        attributes: nextAttributes,
    };
};

export const canUseUpdateEmailAction = (
    features?: AccountEnvironment["features"]
) =>
    Boolean(
        features?.updateEmailFeatureEnabled &&
            features.updateEmailActionEnabled &&
            (!features.isRegistrationEmailAsUsername || features.isEditUserNameAllowed)
    );
